import React, { useState } from 'react';
import {
    Box,
    TextField,
    Typography,
    FormHelperText,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Checkbox,
    FormControlLabel,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslate } from 'react-admin';

interface ServerConfigFormProps {
    config: any;
    onChange: (config: any) => void;
    disabled?: boolean;
}

// Configuration sections with fields - keys will be translated
const getConfigSections = (translate: any) => [
    {
        title: translate('resources.ocpp-servers.config.sections.server'),
        fields: [
            {
                key: 'port',
                label: translate('resources.ocpp-servers.config.fields.port'),
                type: 'number',
                helperText: translate('resources.ocpp-servers.config.helpers.port'),
                min: 1024,
                max: 65535,
                defaultValue: 8888
            },
            {
                key: 'logOcppMessages',
                label: translate('resources.ocpp-servers.config.fields.logOcppMessages'),
                type: 'boolean',
                helperText: translate('resources.ocpp-servers.config.helpers.logOcppMessages'),
                defaultValue: true
            },
            {
                key: 'heartbeatInterval',
                label: translate('resources.ocpp-servers.config.fields.heartbeatInterval'),
                type: 'number',
                helperText: translate('resources.ocpp-servers.config.helpers.heartbeatInterval'),
                min: 10000,
                max: 3600000,
                defaultValue: 30000
            },
            {
                key: 'meterValueInterval',
                label: translate('resources.ocpp-servers.config.fields.meterValueInterval'),
                type: 'number',
                helperText: translate('resources.ocpp-servers.config.helpers.meterValueInterval'),
                min: 10000,
                max: 600000,
                defaultValue: 60000
            },
            {
                key: 'enableAuth',
                label: translate('resources.ocpp-servers.config.fields.enableAuth'),
                type: 'boolean',
                helperText: translate('resources.ocpp-servers.config.helpers.enableAuth'),
                defaultValue: true
            },
            {
                key: 'enableLMS',
                label: translate('resources.ocpp-servers.config.fields.enableLMS'),
                type: 'boolean',
                helperText: translate('resources.ocpp-servers.config.helpers.enableLMS'),
                defaultValue: true
            }
        ]
    },
    {
        title: translate('resources.ocpp-servers.config.sections.lmsCore'),
        fields: [
            {
                key: 'rebalanceInterval',
                label: translate('resources.ocpp-servers.config.fields.rebalanceInterval'),
                type: 'number',
                helperText: translate('resources.ocpp-servers.config.helpers.rebalanceInterval'),
                min: 5000,
                max: 300000,
                defaultValue: 60000
            },
            {
                key: 'minRebalanceInterval',
                label: translate('resources.ocpp-servers.config.fields.minRebalanceInterval'),
                type: 'number',
                helperText: translate('resources.ocpp-servers.config.helpers.minRebalanceInterval'),
                min: 1000,
                max: 60000,
                defaultValue: 5000
            },
            {
                key: 'lmsIecMinimumCurrent',
                label: translate('resources.ocpp-servers.config.fields.lmsIecMinimumCurrent'),
                type: 'number',
                helperText: translate('resources.ocpp-servers.config.helpers.lmsIecMinimumCurrent'),
                min: 6,
                max: 16,
                defaultValue: 6,
                step: 1
            },
            {
                key: 'lmsPracticalMinimumCurrent',
                label: translate('resources.ocpp-servers.config.fields.lmsPracticalMinimumCurrent'),
                type: 'number',
                helperText: translate('resources.ocpp-servers.config.helpers.lmsPracticalMinimumCurrent'),
                min: 6,
                max: 16,
                defaultValue: 10,
                step: 1
            },
            {
                key: 'lmsHysteresisThreshold',
                label: translate('resources.ocpp-servers.config.fields.lmsHysteresisThreshold'),
                type: 'number',
                helperText: translate('resources.ocpp-servers.config.helpers.lmsHysteresisThreshold'),
                min: 0.5,
                max: 5,
                defaultValue: 2,
                step: 0.5
            },
            {
                key: 'lmsProfileRefreshInterval',
                label: translate('resources.ocpp-servers.config.fields.lmsProfileRefreshInterval'),
                type: 'number',
                helperText: translate('resources.ocpp-servers.config.helpers.lmsProfileRefreshInterval'),
                min: 30000,
                max: 600000,
                defaultValue: 60000
            },
            {
                key: 'lmsFailSafeCheckInterval',
                label: translate('resources.ocpp-servers.config.fields.lmsFailSafeCheckInterval'),
                type: 'number',
                helperText: translate('resources.ocpp-servers.config.helpers.lmsFailSafeCheckInterval'),
                min: 3600000,
                max: 172800000,
                defaultValue: 86400000
            }
        ]
    },
    {
        title: translate('resources.ocpp-servers.config.sections.lmsMessage'),
        fields: [
            {
                key: 'lmsMaxRetries',
                label: translate('resources.ocpp-servers.config.fields.lmsMaxRetries'),
                type: 'number',
                helperText: translate('resources.ocpp-servers.config.helpers.lmsMaxRetries'),
                min: 1,
                max: 10,
                defaultValue: 3
            },
            {
                key: 'lmsBaseTimeout',
                label: translate('resources.ocpp-servers.config.fields.lmsBaseTimeout'),
                type: 'number',
                helperText: translate('resources.ocpp-servers.config.helpers.lmsBaseTimeout'),
                min: 5000,
                max: 60000,
                defaultValue: 10000
            },
            {
                key: 'lmsBackoffMultiplier',
                label: translate('resources.ocpp-servers.config.fields.lmsBackoffMultiplier'),
                type: 'number',
                helperText: translate('resources.ocpp-servers.config.helpers.lmsBackoffMultiplier'),
                min: 1,
                max: 3,
                defaultValue: 1.5,
                step: 0.1
            },
            {
                key: 'lmsMaxQueueSize',
                label: translate('resources.ocpp-servers.config.fields.lmsMaxQueueSize'),
                type: 'number',
                helperText: translate('resources.ocpp-servers.config.helpers.lmsMaxQueueSize'),
                min: 10,
                max: 1000,
                defaultValue: 100
            }
        ]
    },
    {
        title: translate('resources.ocpp-servers.config.sections.lmsTime'),
        fields: [
            {
                key: 'lmsDynamicValidityMinutes',
                label: translate('resources.ocpp-servers.config.fields.lmsDynamicValidityMinutes'),
                type: 'number',
                helperText: translate('resources.ocpp-servers.config.helpers.lmsDynamicValidityMinutes'),
                min: 1,
                max: 60,
                defaultValue: 3
            },
            {
                key: 'lmsHeartbeatInterval',
                label: translate('resources.ocpp-servers.config.fields.lmsHeartbeatInterval'),
                type: 'number',
                helperText: translate('resources.ocpp-servers.config.helpers.lmsHeartbeatInterval'),
                min: 10,
                max: 300,
                defaultValue: 60
            },
            {
                key: 'lmsMaxClockDrift',
                label: translate('resources.ocpp-servers.config.fields.lmsMaxClockDrift'),
                type: 'number',
                helperText: translate('resources.ocpp-servers.config.helpers.lmsMaxClockDrift'),
                min: 10,
                max: 120,
                defaultValue: 30
            },
            {
                key: 'lmsValidityBuffer',
                label: translate('resources.ocpp-servers.config.fields.lmsValidityBuffer'),
                type: 'number',
                helperText: translate('resources.ocpp-servers.config.helpers.lmsValidityBuffer'),
                min: 10,
                max: 120,
                defaultValue: 30
            }
        ]
    }
];

export const ServerConfigForm: React.FC<ServerConfigFormProps> = ({
    config = {},
    onChange,
    disabled = false,
}) => {
    const translate = useTranslate();
    const CONFIG_SECTIONS = getConfigSections(translate);
    const [expandedSections, setExpandedSections] = useState<string[]>([CONFIG_SECTIONS[0].title]);

    const handleFieldChange = (key: string, value: any) => {
        onChange({
            ...config,
            [key]: value,
        });
    };

    const handleNumberChange = (key: string, min?: number, max?: number, step?: number) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = event.target.value;
        if (value === '') {
            handleFieldChange(key, undefined);
            return;
        }

        let numValue = step && step < 1 ? parseFloat(value) : parseInt(value, 10);
        if (!isNaN(numValue)) {
            if (min !== undefined && numValue < min) numValue = min;
            if (max !== undefined && numValue > max) numValue = max;
            handleFieldChange(key, numValue);
        }
    };

    const handleBooleanChange = (key: string) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        handleFieldChange(key, event.target.checked);
    };

    const handleAccordionChange = (section: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedSections(prev =>
            isExpanded
                ? [...prev, section]
                : prev.filter(s => s !== section)
        );
    };

    const resetSectionToDefaults = (section: typeof CONFIG_SECTIONS[0]) => {
        const updates = { ...config };
        section.fields.forEach(field => {
            updates[field.key] = field.defaultValue;
        });
        onChange(updates);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
                {translate('resources.ocpp-servers.config.description', {
                    defaultValue: 'Configure advanced OCPP server settings. All values have sensible defaults.'
                })}
            </Typography>

            {CONFIG_SECTIONS.map((section) => (
                <Accordion
                    key={section.title}
                    expanded={expandedSections.includes(section.title)}
                    onChange={handleAccordionChange(section.title)}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                            {section.title}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: { xs: 1, sm: 2 } }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    size="small"
                                    onClick={() => resetSectionToDefaults(section)}
                                    disabled={disabled}
                                >
                                    Reset to Defaults
                                </Button>
                            </Box>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                    gap: 2,
                                }}
                            >
                                {section.fields.map((field) => {
                                    if (field.type === 'number') {
                                        const stepValue = 'step' in field ? field.step : 1;
                                        return (
                                            <TextField
                                                key={field.key}
                                                label={field.label}
                                                type="number"
                                                value={config[field.key] ?? field.defaultValue}
                                                onChange={handleNumberChange(field.key, field.min, field.max, stepValue)}
                                                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                                helperText={field.helperText}
                                                disabled={disabled}
                                                fullWidth
                                                variant="outlined"
                                                InputProps={{
                                                    inputProps: {
                                                        min: field.min,
                                                        max: field.max,
                                                        step: stepValue
                                                    }
                                                }}
                                            />
                                        );
                                    }

                                    if (field.type === 'boolean') {
                                        return (
                                            <Box key={field.key}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={config[field.key] ?? field.defaultValue}
                                                            onChange={handleBooleanChange(field.key)}
                                                            disabled={disabled}
                                                        />
                                                    }
                                                    label={field.label}
                                                />
                                                <FormHelperText>{field.helperText}</FormHelperText>
                                            </Box>
                                        );
                                    }

                                    return null;
                                })}
                            </Box>
                        </Box>
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
};